/**
 * MakeOfferDialog.jsx
 * ====================
 * Modal proposant 2 chemins à l'agent pour créer une offre d'achat :
 *  A. Saisir l'offre maintenant (formulaire direct, agent avec acquéreur en face)
 *  B. Envoyer un lien à l'acquéreur par email (formulaire web public + signature
 *     canvas, l'offre arrive ensuite en "Brouillon à valider")
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Mail, Edit3, ArrowLeft, Send, Loader2, CheckCircle2, Copy, ExternalLink,
} from 'lucide-react';

/* eslint-disable react/no-unescaped-entities */

export default function MakeOfferDialog({ open, onOpenChange, mandateId, mandateNumber, onOfferCreated }) {
  // step: 'choose' | 'invite_form' | 'invite_done' | 'direct_redirect'
  const [step, setStep] = useState('choose');
  const [submitting, setSubmitting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    buyer_email: '',
    buyer_first_name: '',
    buyer_last_name: '',
    personal_message: '',
    validity_days: 10,
  });
  const [inviteResult, setInviteResult] = useState(null);

  const reset = () => {
    setStep('choose');
    setSubmitting(false);
    setInviteForm({ buyer_email: '', buyer_first_name: '', buyer_last_name: '', personal_message: '', validity_days: 10 });
    setInviteResult(null);
  };

  const handleClose = (val) => {
    if (!val) reset();
    onOpenChange(val);
  };

  const sendInvite = async () => {
    if (!inviteForm.buyer_email || !/^.+@.+\..+$/.test(inviteForm.buyer_email)) {
      toast.error("Email de l'acquéreur invalide");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/buyer-offer/invite', {
        mandate_id: mandateId,
        ...inviteForm,
      });
      setInviteResult(res.data);
      setStep('invite_done');
      toast.success("Email envoyé à l'acquéreur");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi de l'invitation");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    if (!inviteResult?.link) return;
    navigator.clipboard.writeText(inviteResult.link);
    toast.success('Lien copié dans le presse-papier');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="make-offer-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            {step !== 'choose' && step !== 'invite_done' && (
              <button onClick={() => setStep('choose')} className="text-muted-foreground hover:text-foreground" aria-label="Retour">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            Faire une offre d'achat
          </DialogTitle>
          {mandateNumber && (
            <DialogDescription>Mandat n°{mandateNumber}</DialogDescription>
          )}
        </DialogHeader>

        {/* === Step 1 : Choose path === */}
        {step === 'choose' && (
          <div className="space-y-3 mt-2">
            <p className="text-sm text-muted-foreground">
              Comment souhaitez-vous formaliser l'offre d'achat ?
            </p>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => setStep('direct_redirect')}
              className="w-full text-left rounded-2xl border border-white/10 hover:border-[#FF914D]/40 bg-card hover:bg-white/[0.04] p-4 transition group"
              data-testid="offer-choice-direct"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793]/30 to-[#681793]/10 flex items-center justify-center flex-shrink-0">
                  <Edit3 className="w-5 h-5 text-[#9b4dca]" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-foreground text-sm">Saisir l'offre maintenant</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Vous êtes avec l'acquéreur ou avez toutes les infos. Vous remplissez le formulaire complet directement.
                  </p>
                </div>
              </div>
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => setStep('invite_form')}
              className="w-full text-left rounded-2xl border border-white/10 hover:border-[#FF914D]/40 bg-card hover:bg-white/[0.04] p-4 transition group"
              data-testid="offer-choice-invite"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF914D]/30 to-[#FF914D]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#FF914D]" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-foreground text-sm">Envoyer un lien à l'acquéreur</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    L'acquéreur reçoit un email avec un formulaire simple. Il remplit + signe sur son téléphone. L'offre arrive en "Brouillon à valider" — vous gardez le contrôle final.
                  </p>
                </div>
              </div>
            </motion.button>
          </div>
        )}

        {/* === Step 2A : Redirect to existing direct flow === */}
        {step === 'direct_redirect' && (
          <DirectOfferRedirect
            mandateId={mandateId}
            onClose={() => handleClose(false)}
            onCreated={onOfferCreated}
          />
        )}

        {/* === Step 2B : Invite form === */}
        {step === 'invite_form' && (
          <div className="space-y-3 mt-2" data-testid="invite-form">
            <p className="text-xs text-muted-foreground">
              L'acquéreur recevra un email avec un lien personnel valable {inviteForm.validity_days} jours.
            </p>

            <div>
              <Label htmlFor="buyer-email" className="text-xs">Email acquéreur *</Label>
              <Input
                id="buyer-email"
                type="email"
                placeholder="acquereur@email.com"
                value={inviteForm.buyer_email}
                onChange={(e) => setInviteForm({ ...inviteForm, buyer_email: e.target.value })}
                data-testid="invite-buyer-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="buyer-first" className="text-xs">Prénom (optionnel)</Label>
                <Input
                  id="buyer-first"
                  value={inviteForm.buyer_first_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, buyer_first_name: e.target.value })}
                  data-testid="invite-buyer-first"
                />
              </div>
              <div>
                <Label htmlFor="buyer-last" className="text-xs">Nom (optionnel)</Label>
                <Input
                  id="buyer-last"
                  value={inviteForm.buyer_last_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, buyer_last_name: e.target.value })}
                  data-testid="invite-buyer-last"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="invite-message" className="text-xs">Message personnel (optionnel)</Label>
              <Textarea
                id="invite-message"
                rows={3}
                placeholder="Ex: « Suite à votre visite, voici le formulaire pour formaliser votre offre... »"
                value={inviteForm.personal_message}
                onChange={(e) => setInviteForm({ ...inviteForm, personal_message: e.target.value })}
                data-testid="invite-message"
              />
            </div>

            <div>
              <Label htmlFor="invite-validity" className="text-xs">Validité du lien (jours)</Label>
              <Input
                id="invite-validity"
                type="number"
                min={1}
                max={30}
                value={inviteForm.validity_days}
                onChange={(e) => setInviteForm({ ...inviteForm, validity_days: Number(e.target.value) || 10 })}
                data-testid="invite-validity"
              />
            </div>

            <Button
              onClick={sendInvite}
              disabled={submitting || !inviteForm.buyer_email}
              className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold"
              data-testid="invite-send-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer l'invitation
            </Button>
          </div>
        )}

        {/* === Step 3 : Invite done === */}
        {step === 'invite_done' && inviteResult && (
          <div className="space-y-4 mt-2" data-testid="invite-done">
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
              <p className="font-bold text-foreground">Invitation envoyée !</p>
              <p className="text-xs text-muted-foreground mt-1">
                {inviteResult.email_mocked
                  ? "Email simulé (Resend désactivé) — partagez le lien manuellement"
                  : `L'acquéreur recevra l'email dans quelques secondes.`}
              </p>
            </div>

            <div className="rounded-xl bg-card border border-white/10 p-3">
              <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Lien direct</p>
              <p className="text-xs text-foreground break-all font-mono">{inviteResult.link}</p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={copyLink} className="flex-1" data-testid="copy-invite-link">
                  <Copy className="w-3 h-3 mr-1.5" /> Copier
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(inviteResult.link, '_blank')} className="flex-1" data-testid="open-invite-link">
                  <ExternalLink className="w-3 h-3 mr-1.5" /> Ouvrir
                </Button>
              </div>
            </div>

            <Button onClick={() => handleClose(false)} className="w-full" data-testid="invite-done-close">
              Fermer
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Sub-component : when user picks "Saisir maintenant", reuse the existing CreateOfferModal
function DirectOfferRedirect({ mandateId, onClose, onCreated }) {
  const [CreateOfferModal, setCreateOfferModal] = useState(null);
  const [loadError, setLoadError] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    import('./CreateOfferModal')
      .then((mod) => {
        if (!cancelled) setCreateOfferModal(() => mod.default);
      })
      .catch((e) => {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('CreateOfferModal load failed', e);
          setLoadError(true);
        }
      });
    return () => { cancelled = true; };
  }, []);

  if (loadError) {
    return (
      <div className="text-center py-8 space-y-3">
        <p className="text-sm text-red-400">Erreur de chargement du formulaire de saisie directe.</p>
        <Button variant="outline" onClick={onClose}>Fermer</Button>
      </div>
    );
  }

  if (!CreateOfferModal) {
    return (
      <div className="text-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF914D] mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Chargement du formulaire…</p>
      </div>
    );
  }

  return (
    <CreateOfferModal
      isOpen={true}
      onClose={onClose}
      onSuccess={() => {
        onCreated?.();
        onClose();
      }}
      preselectedMandateId={mandateId}
    />
  );
}
